/**
 * Google Calendar linking — the mobile half of the authorization-CODE OAuth
 * flow (distinct from google-auth.service.ts's sign-in flow, which uses the
 * implicit grant / ID-token-only response type and can never produce a
 * refresh token). This runs AuthSession with responseType: Code, getting
 * back a short-lived code — NOT tokens — which is POSTed to the backend;
 * the backend (holding the client secret) does the actual code→token
 * exchange server-side. Mobile never sees an access or refresh token.
 */

import Constants from 'expo-constants';
import * as WebBrowser from 'expo-web-browser';
import * as AuthSession from 'expo-auth-session';
import { Platform } from 'react-native';
import { apiService } from './api';

WebBrowser.maybeCompleteAuthSession();

const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar';

function getCalendarClientId(): string {
  const extra = Constants.expoConfig?.extra;
  return extra?.googleCalendarClientId || '';
}

export interface GoogleCalendarInfo {
  id: string;
  summary: string;
  backgroundColor?: string;
}

export interface CalendarProviderAccount {
  _id: string;
  externalAccountEmail: string;
  status: 'active' | 'inactive' | 'expired' | 'revoked';
  isDefault: boolean;
  linkedCalendars: Array<{
    externalCalendarId: string;
    localCalendarId: string;
    syncDirection: 'inbound' | 'outbound' | 'both';
    syncEnabled: boolean;
    lastSyncAt?: string;
  }>;
}

/**
 * Runs the Google consent prompt (authorization-code grant) and links the
 * resulting account server-side. Returns null if the user cancelled.
 * `scope` controls whether this links to the current BUSINESS's calendars
 * (requires general.manageCalendarBookings at edit+, enforced server-side)
 * or the requesting user's own PERSONAL calendars (always allowed).
 */
export async function linkGoogleCalendarAccount(scope: 'business' | 'personal'): Promise<CalendarProviderAccount | null> {
  const clientId = getCalendarClientId();
  if (!clientId) {
    throw new Error('Google Calendar sync is not configured yet');
  }

  const redirectUri = AuthSession.makeRedirectUri();
  console.log('🔍 [calendar-provider.service] redirectUri (register this exact URI in Google Cloud Console → Credentials → your Calendar OAuth client → Authorized redirect URIs):', redirectUri);

  const request = new AuthSession.AuthRequest({
    clientId,
    scopes: ['openid', 'email', CALENDAR_SCOPE],
    redirectUri,
    responseType: AuthSession.ResponseType.Code,
    usePKCE: true,
    extraParams: {
      access_type: 'offline', // required to receive a refresh_token
      prompt: 'consent',      // force the consent screen every time — otherwise Google only returns a refresh_token on first-ever grant
    },
  });

  const result = await request.promptAsync({ authorizationEndpoint: GOOGLE_AUTH_ENDPOINT });

  if (result.type === 'cancel' || result.type === 'dismiss') {
    return null;
  }
  if (result.type !== 'success' || !result.params?.code) {
    throw new Error('Google Calendar authorization failed');
  }

  // usePKCE: true above means Google's authorize request carried a
  // code_challenge derived from this verifier — the token exchange MUST
  // include the matching code_verifier or Google rejects it outright
  // (invalid_grant), regardless of how correct client_id/secret/redirect_uri
  // are. request.codeVerifier is populated by AuthRequest itself once
  // promptAsync has run.
  const codeVerifier = request.codeVerifier;
  if (!codeVerifier) {
    throw new Error('Google Calendar authorization failed (missing PKCE code verifier)');
  }

  const response = await apiService.authenticatedRequest<CalendarProviderAccount>('/api/calendar/providers/google/link', {
    method: 'POST',
    body: JSON.stringify({ code: result.params.code, redirectUri, codeVerifier, scope }),
  });

  if (response.statusCode !== 1000 || !response.data) {
    throw new Error(response.errors?.[0]?.message || 'Failed to link Google Calendar account');
  }
  return response.data;
}

export async function listGoogleAccounts(scope: 'business' | 'personal') {
  return apiService.authenticatedRequest<CalendarProviderAccount[]>(`/api/calendar/providers?scope=${scope}`, { method: 'GET' });
}

export interface CalendarGoogleLink {
  account: CalendarProviderAccount;
  linkedCalendar: CalendarProviderAccount['linkedCalendars'][number];
}

/**
 * Scans every Google account for this scope (business/personal) for a
 * linkedCalendars entry whose localCalendarId matches this specific
 * DISLIX calendar — the calendar-centric view the CalendarForm needs,
 * built on top of the account-centric list the API already returns.
 */
export function findGoogleLinkForCalendar(
  accounts: CalendarProviderAccount[],
  calendarId: string
): CalendarGoogleLink | null {
  for (const account of accounts) {
    const linkedCalendar = account.linkedCalendars.find((lc) => lc.localCalendarId === calendarId);
    if (linkedCalendar) return { account, linkedCalendar };
  }
  return null;
}

export async function listGoogleAccountCalendars(accountId: string) {
  return apiService.authenticatedRequest<GoogleCalendarInfo[]>(`/api/calendar/providers/google/${accountId}/calendars`, { method: 'GET' });
}

export async function linkGoogleCalendar(accountId: string, data: {
  externalCalendarId: string;
  externalCalendarName?: string;
  externalCalendarColor?: string;
  localCalendarId?: string;
  syncDirection: 'inbound' | 'outbound' | 'both';
}) {
  return apiService.authenticatedRequest(`/api/calendar/providers/${accountId}/link-calendar`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateLinkedGoogleCalendar(accountId: string, externalCalendarId: string, updates: { syncEnabled?: boolean; syncDirection?: 'inbound' | 'outbound' | 'both' }) {
  return apiService.authenticatedRequest(`/api/calendar/providers/${accountId}/linked-calendars/${externalCalendarId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function unlinkGoogleAccount(accountId: string) {
  return apiService.authenticatedRequest(`/api/calendar/providers/${accountId}`, { method: 'DELETE' });
}

/** Removes one linked-calendar mapping (unlink or "change calendar") — leaves the Google account connected. */
export async function unlinkGoogleCalendar(accountId: string, externalCalendarId: string) {
  return apiService.authenticatedRequest(`/api/calendar/providers/${accountId}/linked-calendars/${externalCalendarId}`, { method: 'DELETE' });
}
